// src/pages/People.js
import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './People.css';
import '../styles/okr-m3-tokens.css';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { calculateOKRStatus } from '../utils/okrStatusUtils';
import MetricsBar from '../components/okr/MetricsBar';
import StatusFilterChips from '../components/okr/StatusFilterChips';
import ObjectiveCard from '../components/okr/ObjectiveCard';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export default function People() {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [users, setUsers]             = useState([]);
  const [okrs, setOkrs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [expandedMap, setExpandedMap] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [expandedUsers, setExpandedUsers] = useState({});

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [nameInput, setNameInput]   = useState('');
  const [emailInput, setEmailInput] = useState('');

  const formatDate = dateStr => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  // Fetch users & OKRs
  const fetchData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 [People] Starting to fetch users and OKRs...');
      
      // Fetch sequentially to identify which one is failing, but allow partial success
      let usersArray = [];
      let okrsArray = [];
      
      try {
        console.log('🔄 [People] Fetching users...');
        const uRes = await apiGet('/api/users', token);
        console.log('✅ [People] Users response:', uRes.status, uRes.ok);
        
        if (uRes && uRes.ok) {
          const uBody = await uRes.json();
          usersArray = Array.isArray(uBody)
            ? uBody
            : Array.isArray(uBody.users)
              ? uBody.users
              : [];
          console.log('✅ [People] Loaded users:', usersArray.length);
        } else {
          const errorText = uRes ? await uRes.text().catch(() => 'Unknown error') : 'No response';
          console.warn('⚠️ [People] Users fetch failed:', uRes?.status, errorText);
        }
      } catch (err) {
        console.error('❌ [People] Users fetch error:', err);
        // Don't throw - allow page to load with empty users
      }
      
      try {
        console.log('🔄 [People] Fetching OKRs...');
        const oRes = await apiGet('/api/okrs', token);
        console.log('✅ [People] OKRs response:', oRes.status, oRes.ok);
        
        if (oRes && oRes.ok) {
          const oBody = await oRes.json();
          okrsArray = Array.isArray(oBody)
            ? oBody
            : Array.isArray(oBody.okrs)
              ? oBody.okrs
              : [];
          console.log('✅ [People] Loaded OKRs:', okrsArray.length);
        } else {
          const errorText = oRes ? await oRes.text().catch(() => 'Unknown error') : 'No response';
          console.warn('⚠️ [People] OKRs fetch failed:', oRes?.status, errorText);
        }
      } catch (err) {
        console.error('❌ [People] OKRs fetch error:', err);
        // Don't throw - allow page to load with empty OKRs
      }
      
      // If both failed, show error
      if (usersArray.length === 0 && okrsArray.length === 0) {
        throw new Error('Failed to load users and OKRs. Please refresh the page.');
      }

      console.log('✅ [People] Setting state:', { 
        users: usersArray.length, 
        okrs: okrsArray.length 
      });

      setUsers(usersArray);
      setOkrs(okrsArray);

      // init accordion
      const init = {};
      okrsArray.forEach(o => init[o._id] = false);
      setExpandedMap(init);
      
      // Initialize user expansion (first 7 expanded by default)
      const userInit = {};
      usersArray.forEach((u, idx) => {
        const theirOKRs = okrsArray.filter(o =>
          Array.isArray(o.owners)
            ? o.owners.includes(u.name)
            : o.owner === u.name
        );
        userInit[u._id] = theirOKRs.length > 0 && idx < 7;
      });
      setExpandedUsers(userInit);
      
      console.log('✅ [People] Data loaded successfully');

    } catch (err) {
      console.error('❌ [People] Error fetching data:', err);
      setError(err.message || 'Failed to load data');
      setUsers([]);
      setOkrs([]);
    } finally {
      console.log('✅ [People] Setting loading to false');
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleToggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };
  
  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };
  
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Expand/collapse logic
  const toggleExpand = id => setExpandedMap(m => ({ ...m, [id]: !m[id] }));

  // Calculate overall stats
  const { overallProgress, overallScore, totalOkrs, totalKRs } = useMemo(() => {
    const totalOkrs = okrs.length;
    let totalKRs = 0, done = 0, sum = 0;
    okrs.forEach(o => {
      totalKRs += o.keyResults?.length || 0;
      done += o.keyResults?.filter(kr => kr.completed).length || 0;
      sum += o.score || 0;
    });
    const overallProgress = totalKRs ? Math.round((done / totalKRs) * 100) : 0;
    const overallScore = totalOkrs ? sum / totalOkrs : 0;
    return { overallProgress, overallScore, totalOkrs, totalKRs };
  }, [okrs]);

  const handleDelete = async id => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await apiDelete(`/api/users/${id}`, token);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Delete failed (${res.status})`);
      }
      await fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  const openAdd   = () => {
    setEditingId(null);
    setNameInput('');
    setEmailInput('');
    setShowModal(true);
  };
  const openEdit  = user => {
    setEditingId(user._id);
    setNameInput(user.name);
    setEmailInput(user.email);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!nameInput.trim() || !emailInput.trim()) {
      return alert('Please enter both name and email.');
    }

    try {
      let res;
      if (editingId) {
        // update existing user
        res = await apiPut(
          `/api/users/${editingId}`,
          token,
          { name: nameInput.trim(), email: emailInput.trim().toLowerCase() }
        );
      } else {
        // create new user
        res = await apiPost(
          `/api/users`,
          token,
          {
            // No password: the server emails a single-use "set your password" link
            name:     nameInput.trim(),
            email:    emailInput.trim().toLowerCase()
          }
        );
      }

      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || `Save failed (${res.status})`);
      }

      // re-fetch all data to reflect changes
      await fetchData();
      setShowModal(false);
      setEditingId(null);
      setNameInput('');
      setEmailInput('');

    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleKR = async (okrId, krIdx) => {
    const target = okrs.find(o => o._id === okrId);
    if (!target || !target.keyResults || !target.keyResults[krIdx]) {
      console.error('❌ [People] Invalid OKR or key result index:', { okrId, krIdx, target: !!target });
      return;
    }
    
    // Toggle the completed status
    const currentCompleted = target.keyResults[krIdx].completed || false;
    const newCompleted = !currentCompleted;
    
    console.log('🔄 [People] Toggling KR:', { 
      okrId, 
      krIdx, 
      current: currentCompleted, 
      new: newCompleted,
      krText: target.keyResults[krIdx].text?.substring(0, 30)
    });
    
    // Create new keyResults array with updated completed status
    const newKRs = target.keyResults.map((kr, i) => {
      if (i === krIdx) {
        return {
          text: kr.text || '',
          team: kr.team || undefined,
          krOwner: kr.krOwner || undefined,
          completed: newCompleted, // Explicit boolean
          deadline: kr.deadline || undefined
        };
      }
      return {
        text: kr.text || '',
        team: kr.team || undefined,
        krOwner: kr.krOwner || undefined,
        completed: kr.completed === true || kr.completed === 'true' || false, // Ensure boolean
        deadline: kr.deadline || undefined
      };
    });
    
    const done = newKRs.filter(kr => kr.completed === true).length;
    const total = newKRs.length;
    const progress = total ? Math.round((done / total) * 100) : 0;
    const status = calculateOKRStatus(newKRs, target.deadline);
    
    // Prepare payload - only send necessary fields
    const payload = {
      objective: target.objective,
      deadline: target.deadline,
      level: target.level,
      owners: target.owners || [target.owner].filter(Boolean),
      owner: target.owner,
      year: target.year,
      quarter: target.quarter,
      type: target.type,
      keyResults: newKRs,
      progress,
      score: target.score || 0,
      status
    };
    
    console.log('📤 [People] Sending payload:', {
      okrId,
      level: payload.level,
      owners: payload.owners,
      year: payload.year,
      quarter: payload.quarter,
      type: payload.type,
      keyResultsCount: payload.keyResults.length,
      completedCount: payload.keyResults.filter(kr => kr.completed).length,
      progress: payload.progress,
      status: payload.status,
      keyResults: payload.keyResults.map((kr, i) => ({
        idx: i,
        text: kr.text?.substring(0, 30),
        completed: kr.completed,
        team: kr.team,
        krOwner: kr.krOwner
      }))
    });
    
    // Optimistically update UI
    setOkrs(prev => prev.map(o => o._id === okrId ? { ...target, ...payload } : o));
    
    try {
      const res = await apiPut(`/api/okrs/${okrId}`, token, payload);
      if (res.ok) {
        // Update with server response to ensure we have the saved data
        try {
          const savedOKR = await res.json();
          console.log('✅ [People] Saved OKR received:', {
            id: savedOKR._id,
            keyResults: savedOKR.keyResults?.map((kr, i) => ({
              idx: i,
              text: kr.text?.substring(0, 20),
              completed: kr.completed,
              type: typeof kr.completed
            }))
          });
          // Update the OKR in state with the saved version
          setOkrs(prev => prev.map(o => {
            if (o._id === okrId) {
              return savedOKR;
            }
            return o;
          }));
        } catch (parseError) {
          console.error('❌ [People] Error parsing saved OKR response:', parseError);
          // If parsing fails, keep the optimistic update
        }
      } else {
        // Revert on failure
        setOkrs(prev => prev.map(o => o._id === okrId ? target : o));
        let errorMessage = 'Unknown error';
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || JSON.stringify(errorData);
          console.error('❌ [People] Failed to update OKR:', res.status, errorData);
        } catch (parseErr) {
          const errorText = await res.text().catch(() => 'Unknown error');
          errorMessage = errorText;
          console.error('❌ [People] Failed to update OKR (non-JSON):', res.status, errorText);
        }
        alert(`Failed to save changes: ${errorMessage}`);
      }
    } catch (err) {
      // Revert on error
      setOkrs(prev => prev.map(o => o._id === okrId ? target : o));
      console.error('❌ [People] Error updating OKR:', err);
      alert(`Failed to save changes: ${err.message || 'Network error. Please check your connection and try again.'}`);
    }
  };
  
  const handleEditOKR = (id) => {
    navigate(`/editokr/${id}`);
  };
  
  const expandAll = (okrsList) => {
    setExpandedMap(prev => {
      const newMap = { ...prev };
      okrsList.forEach(o => { newMap[o._id] = true; });
      return newMap;
    });
  };
  
  const collapseAll = (okrsList) => {
    setExpandedMap(prev => {
      const newMap = { ...prev };
      okrsList.forEach(o => { newMap[o._id] = false; });
      return newMap;
    });
  };
  
  const handleDeleteOKR = async (id) => {
    if (!window.confirm('Are you sure you want to delete this OKR?')) return;
    try {
      const res = await apiDelete(`/api/okrs/${id}`, token);
      if (res.ok) {
        setOkrs(prev => prev.filter(o => o._id !== id));
      }
    } catch (err) {
      alert('Failed to delete OKR.');
    }
  };

  if (loading) return <div className="m3-center-message">Loading…</div>;
  if (error)   return <div className="m3-center-message m3-error">Error: {error}</div>;

  return (
    <div className="m3-people-okrs-page">
      <div className="m3-page-header">
        <h1 className="m3-page-title">People OKRs</h1>
        <p className="m3-page-subtitle">All OKRs grouped by user</p>
      </div>

      {/* Metrics Bar */}
      {okrs.length > 0 && (
        <MetricsBar
          progress={overallProgress}
          score={overallScore}
          objectives={totalOkrs}
          keyResults={totalKRs}
        />
      )}

      {/* Status Filter Chips */}
      <StatusFilterChips
        selectedStatuses={selectedStatuses}
        onToggleStatus={handleToggleStatus}
      />

      {/* Users with OKRs */}
      <div className="m3-users-list">
        {users.map(userObj => {
          const theirOKRs = okrs.filter(o => {
            const okrWithStatus = {
              ...o,
              status: calculateOKRStatus(o.keyResults || [], o.deadline)
            };
            
            // Filter by selected statuses if any
            if (selectedStatuses.length > 0 && !selectedStatuses.includes(okrWithStatus.status)) {
              return false;
            }
            
            return Array.isArray(o.owners)
              ? o.owners.includes(userObj.name)
              : o.owner === userObj.name;
          });
          
          const isUserExpanded = expandedUsers[userObj._id] !== false;
          
          // Calculate user stats (handle users with no OKRs)
          const onTrack = theirOKRs.length > 0 ? theirOKRs.filter(o => {
            const status = calculateOKRStatus(o.keyResults || [], o.deadline);
            return status === 'On Track';
          }).length : 0;
          const atRisk = theirOKRs.length > 0 ? theirOKRs.filter(o => {
            const status = calculateOKRStatus(o.keyResults || [], o.deadline);
            return status === 'At Risk';
          }).length : 0;
          const behind = theirOKRs.length > 0 ? theirOKRs.filter(o => {
            const status = calculateOKRStatus(o.keyResults || [], o.deadline);
            return status === 'Behind';
          }).length : 0;
          const userStats = { onTrack, atRisk, behind, total: theirOKRs.length };
          
          return (
            <div key={userObj._id} className="m3-user-card">
              <div className="m3-user-card-header" onClick={() => toggleUserExpand(userObj._id)}>
                <div className="m3-user-avatar">
                  {getInitials(userObj.name)}
                </div>
                <div className="m3-user-info">
                  <h2 className="m3-user-name">{userObj.name}</h2>
                  <div className="m3-user-stats">
                    <span className="m3-user-stat-chip">Objectives: {userStats.total}</span>
                    <span className="m3-user-stat-chip">On Track: {userStats.onTrack}</span>
                    <span className="m3-user-stat-chip">At Risk: {userStats.atRisk}</span>
                    <span className="m3-user-stat-chip">Behind: {userStats.behind}</span>
                  </div>
                </div>
                <button className="m3-expand-user-button">
                  {isUserExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>
              
              {isUserExpanded && (
                <div className="m3-user-okrs">
                  {theirOKRs.length > 0 ? (
                    <>
                      <div className="m3-expand-controls">
                        <button className="m3-button m3-button-text" onClick={() => expandAll(theirOKRs)}>
                          Expand All
                        </button>
                        <button className="m3-button m3-button-text" onClick={() => collapseAll(theirOKRs)}>
                          Collapse All
                        </button>
                      </div>
                      <div className="m3-okrs-list">
                        {theirOKRs.map(okr => (
                          <ObjectiveCard
                            key={okr._id}
                            okr={{
                              ...okr,
                              status: calculateOKRStatus(okr.keyResults || [], okr.deadline)
                            }}
                            isExpanded={expandedMap[okr._id] || false}
                            onToggleExpand={toggleExpand}
                            onToggleKR={handleToggleKR}
                            onEdit={handleEditOKR}
                            onDelete={handleDeleteOKR}
                            canEdit={true}
                            canDelete={true}
                          />
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="m3-empty-user-okrs">
                      <p>No OKRs for this user.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
